import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, SelectField, SelectItem, Button } from "@kuda/ui";
import SelectCluster from "../selectCluster";
import { getClusterProviders } from "../config";

/**
 * Clones every selected secret into one target cluster/provider, keeping
 * each one's own name, value, and Environment/Team tags exactly as-is —
 * unlike the single-secret Duplicate dialog, there's no per-item renaming
 * or re-tagging here since it applies to the whole selection at once.
 */
const BulkCloneDialog = ({ open, onClose, count, defaultCluster, userRole, onConfirm }) => {
  const [targetCluster, setTargetCluster] = useState(defaultCluster || "");
  const [targetProvider, setTargetProvider] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setTargetCluster(defaultCluster || "");
      setError("");
    }
  }, [open, defaultCluster]);

  const providersForCluster = useMemo(
    () => (targetCluster ? getClusterProviders(targetCluster) : []),
    [targetCluster]
  );

  useEffect(() => {
    setTargetProvider(providersForCluster[0]?.platformName || "");
  }, [providersForCluster]);

  const canSubmit = !!targetCluster && !!targetProvider;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      await onConfirm({ targetCluster, targetProvider });
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to clone secrets.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !loading) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Clone {count} secret{count === 1 ? "" : "s"} to another cluster</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <p className="text-sm text-color-text-secondary">
            Each secret keeps its own name, value, and Environment/Team tags — only the destination changes.
          </p>

          <SelectCluster
            key={`${open}`}
            onClusterChange={setTargetCluster}
            label="Target cluster"
            defaultCluster={targetCluster}
            showAll={true}
            userRole={userRole}
          />

          {providersForCluster.length > 1 && (
            <SelectField
              label="Provider"
              placeholder="Select provider"
              value={targetProvider}
              onValueChange={setTargetProvider}
            >
              {providersForCluster.map((p) => (
                <SelectItem key={p.platformName} value={p.platformName}>
                  {p.platformName.toUpperCase()}
                </SelectItem>
              ))}
            </SelectField>
          )}

          {error && <p className="text-sm text-color-text-state-error-default">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="text" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="filled" onClick={handleConfirm} loading={loading} disabled={!canSubmit}>
            Clone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkCloneDialog;
